import { generateGeoJson } from '@/map/geojson-gen';
import { MapboxInstanceMgr } from '@/map/map-instance';

/**
 * Displays a pulsing dot on a map
 */
export class PulsingDot {
  static FRAMES_PER_SECOND = 15;

  static INITIAL_OPACITY = 1;

  static INITIAL_RADIUS = 7;

  static MAX_RADIUS = 28;

  static ANIMATION_CYCLE_LENGTH = 1000; // milliseconds

  static BASE_SOURCE_ID = 'SOURCES.PULSING_DOT';

  static BASE_INNER_LAYER_ID = 'LAYERS.PULSING_DOT_INNER';

  static BASE_OUTER_LAYER_ID = 'LAYERS.PULSING_DOT_OUTER';

  /**
   *
   * @param {Map} map mapbox Map
   * @param {object} propertyData
   */
  constructor(propertyData) {
    this.radius = PulsingDot.INITIAL_RADIUS;
    this.opacity = PulsingDot.INITIAL_OPACITY;
    this.animateId = null;
    this.fpsInterval =
      PulsingDot.ANIMATION_CYCLE_LENGTH / PulsingDot.FRAMES_PER_SECOND; // ms
    this.pulsingStartingTime = null;
    this.data = propertyData;
    this.geojson = generateGeoJson([propertyData]);
    this.deckardid = propertyData.deckardid;
    this.stopCalled = false;
  }

  get map() {
    // eslint-disable-line class-methods-use-this
    return MapboxInstanceMgr.getInstance().mapInstance;
  }

  /*
   * Namespacing the ids so that we can have multiple dots on the map with
   * no collisions
   */

  get sourceId() {
    return `${PulsingDot.BASE_SOURCE_ID}-${this.deckardid}`;
  }

  get innerLayerId() {
    return `${PulsingDot.BASE_INNER_LAYER_ID}-${this.deckardid}`;
  }

  get outerLayerId() {
    return `${PulsingDot.BASE_OUTER_LAYER_ID}-${this.deckardid}`;
  }

  get innerLayer() {
    return {
      id: this.innerLayerId,
      source: this.sourceId,
      type: 'circle',
      paint: {
        'circle-radius': PulsingDot.INITIAL_RADIUS,
        'circle-color': '#37A3BC',
      },
    };
  }

  get outerLayer() {
    return {
      id: this.outerLayerId,
      source: this.sourceId,
      type: 'circle',
      paint: {
        'circle-radius': PulsingDot.INITIAL_RADIUS,
        'circle-radius-transition': { duration: 0 },
        'circle-opacity-transition': { duration: 0 },
        'circle-color': '#37A3BC',
      },
    };
  }

  addSource() {
    this.map.addSource(this.sourceId, {
      type: 'geojson',
      data: this.geojson,
    });
  }

  removeSource() {
    if (this.map) {
      this.map.removeSource(this.sourceId);
    }
  }

  addLayers() {
    this.map.addLayer(this.innerLayer);
    this.map.addLayer(this.outerLayer);
  }

  removeLayers() {
    if (this.map) {
      this.map.removeLayer(this.innerLayerId);
      this.map.removeLayer(this.outerLayerId);
    }
  }

  animate() {
    if (!this.map) return; // map may be null inside this callback when map destroyed
    this.animateId = requestAnimationFrame(this.animate.bind(this));

    const now = Date.now();
    if (now - this.pulsingStartingTime < this.fpsInterval) {
      return;
    }
    this.pulsingStartingTime = now;

    /*
     * Prevents error from race condition. Animation frame may call `animate()` one more
     * time after clearInterval executed
     */
    if (this.stopCalled) return;

    this.radius +=
      (PulsingDot.MAX_RADIUS - this.radius) / PulsingDot.FRAMES_PER_SECOND;
    this.opacity -= 0.9 / PulsingDot.FRAMES_PER_SECOND;

    if (this.opacity <= 0) {
      this.radius = PulsingDot.INITIAL_RADIUS;
      this.opacity = PulsingDot.INITIAL_OPACITY;
    }

    this.map.setPaintProperty(this.outerLayerId, 'circle-radius', this.radius);
    this.map.setPaintProperty(
      this.outerLayerId,
      'circle-opacity',
      this.opacity
    );
  }

  /**
   * Starts animating the pulsing dot
   */
  initializeAnimation() {
    this.pulsingStartingTime = Date.now();
    this.animateId = requestAnimationFrame(this.animate.bind(this));
  }

  start() {
    this.addSource();
    this.addLayers();
    this.initializeAnimation();
  }

  stop() {
    this.stopCalled = true;
    cancelAnimationFrame(this.animateId);
    this.removeLayers();
    this.removeSource();
  }
}
